import { useRef, useState } from "react";
import { Upload, FileJson, AlertTriangle, Smartphone, Tablet, Wand2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import type {
  HomeScreenConfig,
  HomeScreenLayout,
  IconItem,
  SmplifyExtension,
} from "./types";
import { readExtension, buildConfig } from "./projection";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (next: Record<string, unknown>) => void;
}

/**
 * Import preset controls how a bare top-level Pages/Dock layout is mapped
 * into form-factor variants. When the source already has an `x-smplify`
 * extension, presets only override the active `target` selection.
 *
 * - `auto`:   keeps source target if present, else treats input as iPhone.
 * - `iphone`: forces top-level layout into the iPhone variant.
 * - `ipad`:   forces top-level layout into the iPad variant.
 * - `both`:   mirrors top-level layout into BOTH variants so the user can
 *             then tweak each independently.
 */
export type ImportPreset = "auto" | "iphone" | "ipad" | "both";

/**
 * Validate and normalize a persisted IconKind verdict.
 *
 * Rules (strict — anything else is dropped, NOT coerced):
 *  - Must be the literal string `"favicon"` or `"generic"`.
 *  - A `"favicon"` verdict requires a non-empty string `Icon` URL; otherwise
 *    it is meaningless (the renderer would have nothing to show) and we
 *    drop it so the component re-probes via `WebClipFavicon`.
 *  - Anything else (numbers, booleans, objects, arrays, casing variants like
 *    `"Favicon"`, unknown strings) → `undefined`.
 */
export function sanitizeIconKind(
  rawKind: unknown,
  rawIcon: unknown
): "favicon" | "generic" | undefined {
  if (rawKind === "generic") return "generic";
  if (rawKind === "favicon") {
    return typeof rawIcon === "string" && rawIcon.trim().length > 0
      ? "favicon"
      : undefined;
  }
  return undefined;
}

/** Validate an IconItem shape; returns null if invalid. */
export function sanitizeItem(raw: unknown): IconItem | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const type = r.Type;
  if (type === "Application" && typeof r.BundleID === "string") {
    return {
      Type: "Application",
      BundleID: r.BundleID,
      DisplayName: typeof r.DisplayName === "string" ? r.DisplayName : undefined,
    };
  }
  if (type === "WebClip" && typeof r.URL === "string") {
    const Icon = typeof r.Icon === "string" ? r.Icon : undefined;
    return {
      Type: "WebClip",
      URL: r.URL,
      DisplayName: typeof r.DisplayName === "string" ? r.DisplayName : undefined,
      Icon,
      // Preserve the favicon-vs-generic verdict so imported clips render
      // exactly as they did in the source layout — but only when the verdict
      // is a known literal AND consistent with the accompanying Icon field.
      IconKind: sanitizeIconKind(r.IconKind, Icon),
    };
  }
  if (type === "Folder" && typeof r.DisplayName === "string" && Array.isArray(r.Pages)) {
    const pages = (r.Pages as unknown[])
      .map((p) =>
        Array.isArray(p)
          ? (p.map(sanitizeItem).filter(Boolean) as IconItem[])
          : []
      );
    return { Type: "Folder", DisplayName: r.DisplayName, Pages: pages };
  }
  return null;
}

function sanitizeLayout(raw: unknown): HomeScreenLayout | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.Pages)) return null;
  const Pages = (r.Pages as unknown[]).map((p) =>
    Array.isArray(p) ? (p.map(sanitizeItem).filter(Boolean) as IconItem[]) : []
  );
  const Dock = Array.isArray(r.Dock)
    ? ((r.Dock as unknown[]).map(sanitizeItem).filter(Boolean) as IconItem[])
    : [];
  return { Pages: Pages.length ? Pages : [[]], Dock };
}

/**
 * Parse arbitrary JSON into a fully normalized HomeScreenConfig.
 * Preserves the exact order of Pages, Dock and folder contents.
 *
 * Behavior by preset:
 *  - `auto`: respect source x-smplify if present; else map top-level to iPhone.
 *  - `iphone` / `ipad`: force top-level Pages/Dock into that variant.
 *  - `both`: mirror top-level Pages/Dock into both variants.
 */
function parseImport(
  text: string,
  preset: ImportPreset
): { config: HomeScreenConfig; counts: { iphone?: number; ipad?: number } } {
  const trimmed = text.trim();
  if (!trimmed) throw new Error("Paste or upload a JSON layout to import.");
  let raw: unknown;
  try {
    raw = JSON.parse(trimmed);
  } catch {
    throw new Error("Invalid JSON — check for missing commas or quotes.");
  }
  if (!raw || typeof raw !== "object") {
    throw new Error("JSON root must be an object.");
  }

  const r = raw as Record<string, unknown>;
  const hasExt = !!r["x-smplify"];
  const hasTopLayout = Array.isArray(r.Pages);

  if (!hasExt && !hasTopLayout) {
    throw new Error('JSON must contain a "Pages" array or an "x-smplify" extension.');
  }

  // Read the source extension as the starting point so we don't lose existing
  // per-variant data when the user just wants to retarget.
  const sourceExt = readExtension(r as unknown as HomeScreenConfig);
  const cleanedVariants: SmplifyExtension["variants"] = {};
  if (sourceExt.variants.iphone) {
    const c = sanitizeLayout(sourceExt.variants.iphone);
    if (c) cleanedVariants.iphone = c;
  }
  if (sourceExt.variants.ipad) {
    const c = sanitizeLayout(sourceExt.variants.ipad);
    if (c) cleanedVariants.ipad = c;
  }

  // Top-level layout (Apple shape) — used by presets to override variants.
  const topLayout = hasTopLayout ? sanitizeLayout(r) : null;

  let target: SmplifyExtension["target"] = sourceExt.target;
  if (preset === "iphone") {
    if (topLayout) cleanedVariants.iphone = topLayout;
    target = "iphone";
  } else if (preset === "ipad") {
    if (topLayout) cleanedVariants.ipad = topLayout;
    target = "ipad";
  } else if (preset === "both") {
    if (topLayout) {
      cleanedVariants.iphone = topLayout;
      // Deep-clone so editing one variant doesn't mutate the other.
      cleanedVariants.ipad = JSON.parse(JSON.stringify(topLayout)) as HomeScreenLayout;
    }
    target = "both";
  } else {
    // auto: only fill iPhone from top-level when there's no x-smphony source.
    if (!hasExt && topLayout) {
      cleanedVariants.iphone = topLayout;
      target = "iphone";
    }
  }

  if (!cleanedVariants.iphone && !cleanedVariants.ipad) {
    throw new Error("No valid Pages/Dock data found after parsing.");
  }

  const cleanedExt: SmplifyExtension = { target, variants: cleanedVariants };

  const counts = {
    iphone: cleanedVariants.iphone
      ? cleanedVariants.iphone.Pages.reduce((n, p) => n + p.length, 0)
      : undefined,
    ipad: cleanedVariants.ipad
      ? cleanedVariants.ipad.Pages.reduce((n, p) => n + p.length, 0)
      : undefined,
  };

  return { config: buildConfig(cleanedExt), counts };
}

const PRESETS: Array<{
  id: ImportPreset;
  label: string;
  hint: string;
  Icon: typeof Smartphone | typeof Tablet | typeof Wand2;
}> = [
  { id: "auto", label: "Auto-detect", hint: "Use source target", Icon: Wand2 },
  { id: "iphone", label: "iPhone", hint: "Map top-level → iPhone", Icon: Smartphone },
  { id: "ipad", label: "iPad", hint: "Map top-level → iPad", Icon: Tablet },
  { id: "both", label: "Both", hint: "Mirror to iPhone & iPad", Icon: Wand2 },
];

export function ImportLayoutDialog({ open, onOpenChange, onImport }: Props) {
  const [text, setText] = useState("");
  const [preset, setPreset] = useState<ImportPreset>("auto");
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function reset() {
    setText("");
    setPreset("auto");
    setError(null);
  }

  function handleFile(file: File) {
    if (file.size > 1024 * 1024) {
      setError("File too large (max 1 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      setText(result);
      setError(null);
    };
    reader.onerror = () => setError("Could not read file.");
    reader.readAsText(file);
  }

  function handleImport() {
    try {
      const { config, counts } = parseImport(text, preset);
      onImport(config as unknown as Record<string, unknown>);
      const parts: string[] = [];
      if (counts.iphone !== undefined) parts.push(`${counts.iphone} iPhone icons`);
      if (counts.ipad !== undefined) parts.push(`${counts.ipad} iPad icons`);
      toast.success(`Layout imported${parts.length ? ` — ${parts.join(", ")}` : ""}`);
      reset();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to import layout.");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileJson className="h-5 w-5" /> Import layout
          </DialogTitle>
          <DialogDescription>
            Paste a JSON home screen layout or upload a <code>.json</code> file. The exact page
            and dock ordering is preserved. Existing edits in the editor will be replaced.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-foreground">Import as</div>
            <div
              role="radiogroup"
              aria-label="Import preset"
              className="grid grid-cols-2 sm:grid-cols-4 gap-2"
            >
              {PRESETS.map((p) => {
                const Icon = p.Icon;
                const active = preset === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setPreset(p.id)}
                    className={cn(
                      "flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      active
                        ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                        : "border-border bg-background"
                    )}
                  >
                    <span className="flex items-center gap-1.5 text-xs font-medium">
                      <Icon className="h-3.5 w-3.5" />
                      {p.label}
                    </span>
                    <span className="text-[11px] text-muted-foreground">{p.hint}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              className="gap-1"
            >
              <Upload className="h-4 w-4" /> Upload .json
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
                e.target.value = "";
              }}
            />
            <span className="text-xs text-muted-foreground">or paste below</span>
          </div>

          <Textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              if (error) setError(null);
            }}
            placeholder={`{\n  "Pages": [[{ "Type": "Application", "BundleID": "com.apple.mobilesafari" }]],\n  "Dock": []\n}`}
            className="font-mono text-xs min-h-[260px]"
            spellCheck={false}
          />

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!text.trim()}>
            Import layout
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
